import { useCallback } from 'react';
import { trpc } from '../lib/trpc';
import { useWorkflowStore } from '../lib/workflow-store';
import type { CreateWorkflowInput, UpdateWorkflowInput } from '../lib/workflow-types';

export function useWorkflows() {
  const { workflows, runs, loading, error, setWorkflows, setRuns, addWorkflow, updateWorkflow, removeWorkflow, setLoading, setError } = useWorkflowStore();

  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await trpc.workflow.list.query();
      setWorkflows(result as any);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch workflows');
    } finally {
      setLoading(false);
    }
  }, [setWorkflows, setLoading, setError]);

  const fetchRuns = useCallback(async (workflowId?: string) => {
    setLoading(true);
    try {
      if (workflowId) {
        const result = await trpc.workflow.getRuns.query({ workflowId });
        setRuns(result as any);
      } else {
        const result = await trpc.workflow.listRuns.query();
        setRuns(result as any);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [setRuns, setLoading, setError]);

  const createWorkflow = useCallback(async (input: CreateWorkflowInput) => {
    setLoading(true);
    try {
      const workflow = await trpc.workflow.create.mutate(input);
      addWorkflow(workflow as any);
      return workflow;
    } catch (err: any) {
      setError(err.message || 'Failed to create workflow');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [addWorkflow, setLoading, setError]);

  const editWorkflow = useCallback(async (id: string, data: UpdateWorkflowInput) => {
    try {
      const updated = await trpc.workflow.update.mutate({ id, data });
      updateWorkflow(id, data as any);
      return updated;
    } catch (err: any) {
      setError(err.message || 'Failed to update workflow');
      throw err;
    }
  }, [updateWorkflow, setError]);

  const deleteWorkflow = useCallback(async (id: string) => {
    try {
      await trpc.workflow.delete.mutate({ id });
      removeWorkflow(id);
    } catch (err: any) {
      setError(err.message || 'Failed to delete workflow');
      throw err;
    }
  }, [removeWorkflow, setError]);

  const triggerWorkflow = useCallback(async (workflowId: string, input?: Record<string, string>) => {
    try {
      const run = await trpc.workflow.trigger.mutate({ workflowId, input });
      return run;
    } catch (err: any) {
      setError(err.message || 'Failed to trigger workflow');
      throw err;
    }
  }, [setError]);

  return {
    workflows,
    runs,
    loading,
    error,
    fetchWorkflows,
    fetchRuns,
    createWorkflow,
    editWorkflow,
    deleteWorkflow,
    triggerWorkflow,
  };
}
